'use client';

import Link from 'next/link';
import { useState, Suspense, useEffect } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { LogOut, Settings, MessageCircle, Menu, X } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { signOut } from '@/app/[locale]/(login)/actions';
import { User } from '@/lib/db/schema';
import useSWR, { mutate } from 'swr';
import { Sidebar } from '@/components/interface/Sidebar';
import { MobileBottomNav } from '@/components/interface/MobileBottomNav';
import Logo from '@/components/interface/Logo';
import { ThemeSwitcher } from '@/components/theme-switcher';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { PresenceTracker } from '@/components/presence/PresenceTracker';
import { NewChatAlertListener } from '@/components/chat/NewChatAlertListener';
import { FloatingChatProvider } from '@/components/chat/FloatingChatContext';
import { FloatingChatDock } from '@/components/chat/FloatingChatDock';
import { useInstanceStatus } from '@/lib/hooks/useInstanceStatus';
import { PermissionGuard } from '@/components/auth/PermissionGuard';

const fetcher = (url: string) => fetch(url).then((res) => res.json());

function UserMenu() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { data: user } = useSWR<User>('/api/user', fetcher);
  const router = useRouter();

  async function handleSignOut() {
    await signOut();
    mutate('/api/user');
    router.push('/');
  }

  if (!user) {
    return (
      <div className="flex items-center gap-2">
        <Button asChild variant="ghost" size="sm" className="rounded-xl text-xs font-semibold hover:bg-muted text-muted-foreground hover:text-foreground">
          <Link href="/sign-in">Sign In</Link>
        </Button>
        <Button asChild size="sm" className="rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground text-xs font-bold shadow-md shadow-primary/20 px-4">
          <Link href="/sign-up">Start Free Trial →</Link>
        </Button>
      </div>
    );
  }

  return (
    <DropdownMenu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
      <DropdownMenuTrigger>
        <Avatar className="cursor-pointer size-8 border border-border">
          <AvatarImage alt={user.name || ''} />
          <AvatarFallback className="bg-primary/10 text-primary text-xs">
            {user.email
              .split(' ')
              .map((n) => n[0])
              .join('')}
          </AvatarFallback>
        </Avatar>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="flex flex-col gap-1 w-48">
        <DropdownMenuItem className="cursor-pointer">
          <Link href="/dashboard" className="flex w-full items-center">
            <MessageCircle className="mr-2 h-4 w-4" />
            <span>Dashboard</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem className="cursor-pointer">
          <Link href="/settings" className="flex w-full items-center">
            <Settings className="mr-2 h-4 w-4" />
            <span>Settings</span>
          </Link>
        </DropdownMenuItem>
        <form action={handleSignOut} className="w-full">
          <button type="submit" className="flex w-full">
            <DropdownMenuItem className="w-full flex-1 cursor-pointer text-destructive focus:text-destructive focus:bg-destructive/10">
              <LogOut className="mr-2 h-4 w-4" />
              <span>Sign out</span>
            </DropdownMenuItem>
          </button>
        </form>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 border-b border-border/50 bg-background/80 backdrop-blur-xl transition-all">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        {/* Logo */}
        <Link href="/" className="flex items-center shrink-0">
          <Logo />
        </Link>

        {/* Center Modern Navigation Links (Desktop) */}
        <nav className="hidden lg:flex items-center gap-1 text-xs font-semibold text-muted-foreground bg-muted/40 px-3 py-1.5 rounded-full border border-border/60 backdrop-blur-xs">
          <a
            href="#interactive-demo"
            className="px-3 py-1 rounded-full hover:text-foreground hover:bg-background/80 transition-colors"
          >
            Live Simulator
          </a>
          <a
            href="#workflow"
            className="px-3 py-1 rounded-full hover:text-foreground hover:bg-background/80 transition-colors"
          >
            Workflows
          </a>
          <a
            href="#features"
            className="px-3 py-1 rounded-full hover:text-foreground hover:bg-background/80 transition-colors"
          >
            Features
          </a>
          <a
            href="#pricing"
            className="px-3 py-1 rounded-full hover:text-foreground hover:bg-background/80 transition-colors"
          >
            Pricing
          </a>
          <a
            href="#faq"
            className="px-3 py-1 rounded-full hover:text-foreground hover:bg-background/80 transition-colors"
          >
            FAQ
          </a>
        </nav>
        
        {/* Right Actions */}
        <div className="hidden md:flex items-center space-x-2.5">
          <LanguageSwitcher />
          <ThemeSwitcher />
          <Suspense fallback={<div className="h-8 w-8 bg-muted rounded-full animate-pulse" />}>
            <UserMenu />
          </Suspense>
        </div>

        {/* Mobile Hamburger */}
        <div className="md:hidden flex items-center gap-2">
          <LanguageSwitcher />
          <ThemeSwitcher />
          <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
            {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
      </div>

      {/* Mobile Drawer */}
      {isMobileMenuOpen && (
        <div className="md:hidden border-t border-border bg-background/95 backdrop-blur-md p-4 flex flex-col gap-3 animate-in slide-in-from-top-2 duration-200">
          <div className="flex flex-col gap-1 pb-3 border-b border-border/60 text-sm font-semibold text-muted-foreground">
            <a
              href="#interactive-demo"
              onClick={() => setIsMobileMenuOpen(false)}
              className="px-3 py-2 rounded-lg hover:bg-muted hover:text-foreground"
            >
              Live Simulator
            </a>
            <a
              href="#workflow"
              onClick={() => setIsMobileMenuOpen(false)}
              className="px-3 py-2 rounded-lg hover:bg-muted hover:text-foreground"
            >
              Workflow Automation
            </a>
            <a
              href="#features"
              onClick={() => setIsMobileMenuOpen(false)}
              className="px-3 py-2 rounded-lg hover:bg-muted hover:text-foreground"
            >
              Omnichannel Features
            </a>
            <a
              href="#pricing"
              onClick={() => setIsMobileMenuOpen(false)}
              className="px-3 py-2 rounded-lg hover:bg-muted hover:text-foreground"
            >
              Pricing
            </a>
            <a
              href="#faq"
              onClick={() => setIsMobileMenuOpen(false)}
              className="px-3 py-2 rounded-lg hover:bg-muted hover:text-foreground"
            >
              FAQ
            </a>
          </div>
          <Suspense>
            <UserMenu />
          </Suspense>
        </div>
      )}
    </header>
  );
}

export default function Layout({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const isHomePage = pathname === '/' || /^\/[a-z]{2}$/.test(pathname);
  
  const { data: team } = useSWR('/api/team', fetcher);
  const { hasInstance, isLoading: isInstanceLoading } = useInstanceStatus();

  const pathnameWithoutLocale = pathname.replace(/^\/[a-z]{2}(?=\/)/, '');
  const isLockedRoute = ['/pipeline', '/contacts', '/automation'].some(
    (route) => pathnameWithoutLocale === route || pathnameWithoutLocale.startsWith(route + '/')
  );

  useEffect(() => {
    if (team && typeof team === 'object' && 'id' in team && !team.planId) {
      if (!isHomePage && !pathname.startsWith('/pricing')) {
        router.push('/pricing');
      }
    }
  }, [team, pathname, isHomePage, router]);

  useEffect(() => {
    if (!isInstanceLoading && !hasInstance && isLockedRoute) {
      router.push('/dashboard');
    }
  }, [isInstanceLoading, hasInstance, isLockedRoute, router]);

  if (isHomePage) {
    return (
      <FloatingChatProvider>
        <div className="flex flex-col min-h-screen bg-background">
          <PresenceTracker />
          <NewChatAlertListener />
          <FloatingChatDock />
          <Header />
          <main className="flex-1">
              {children}
          </main>
        </div>
      </FloatingChatProvider>
    );
  }

  const isChatConversation = pathname.includes('/dashboard/chat/');

  return (
    <FloatingChatProvider>
      <div className="fixed inset-0 flex h-full w-full bg-muted overflow-hidden overscroll-none">
        <PresenceTracker />
        <NewChatAlertListener />
        <FloatingChatDock />
        <Sidebar />
        <main
          className={`flex-1 flex flex-col h-full min-h-0 overflow-hidden relative ${
            isChatConversation ? 'pb-0' : 'pb-[calc(4rem+env(safe-area-inset-bottom))]'
          } md:pb-0`}
        >
          <PermissionGuard>
            {children}
          </PermissionGuard>
        </main>
        <MobileBottomNav />
      </div>
    </FloatingChatProvider>
  );
}